import { useCallback } from 'react';
import { QuestionData } from '../types/questionData';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { QuestionDataLocalStorage } from '../lib/localStorage/questionDataLocalStorage';

interface UseQuestionStorageProps {
  userInfo: any;
  tenantId: number | null;
  qbTestId: string | null;
  isWebViewMode: boolean;
}

interface UseQuestionStorageReturn {
  saveQuestionData: (questionId: string, data: Partial<QuestionData>) => Promise<void>;
  loadQuestionData: (questionId: string) => Promise<QuestionData | null>;
  syncToFirebase: () => Promise<void>;
}

export function useQuestionStorage({
  userInfo,
  tenantId,
  qbTestId,
  isWebViewMode
}: UseQuestionStorageProps): UseQuestionStorageReturn {
  const { 
    getStorageService,
    updateQuestionDataCache,
    addPendingSave,
    getPendingSaves,
    clearPendingSaves
  } = useWhiteboardStore();
  
  const saveQuestionData = useCallback(async (questionId: string, data: Partial<QuestionData>) => {
    if (!userInfo?.id || !tenantId || !qbTestId) return;
    
    console.log('🔍 useQuestionStorage - saveQuestionData called with:', {
      userInfoId: userInfo.id,
      userInfoIdType: typeof userInfo.id,
      tenantId,
      qbTestId,
      questionId,
      isWebViewMode
    });
    
    try {
      // Create full QuestionData object for localStorage
      const fullData: QuestionData = {
        questionId,
        userId: userInfo.id,
        tenantId,
        testId: qbTestId,
        updatedAt: Date.now(),
        ...data
      } as QuestionData;
      
      // Save to localStorage first
      QuestionDataLocalStorage.saveQuestionData(fullData);
      
      // Update cache with the full data object
      updateQuestionDataCache(questionId, fullData);
      
      // Add to pending saves for Firebase sync
      if (!isWebViewMode) {
        addPendingSave(questionId, fullData);
      }
    } catch (error) {
    }
  }, [userInfo, tenantId, qbTestId, isWebViewMode, updateQuestionDataCache, addPendingSave]);
  
  const loadQuestionData = useCallback(async (questionId: string): Promise<QuestionData | null> => {
    if (!userInfo?.id || !tenantId || !qbTestId) return null;
    
    try {
      // Try localStorage first
      const userId = userInfo.id;
      const localData = QuestionDataLocalStorage.getQuestionData(
        tenantId,
        userId,
        qbTestId,
        questionId
      );
      
      if (localData) {
        return localData;
      }
      
      // Try Firebase if available
      const storageService = getStorageService();
      if (storageService && !isWebViewMode) {
        try {
          const firebaseData = await storageService.getQuestionData(
            userInfo.id,
            tenantId,
            qbTestId,
            questionId
          );
          
          if (firebaseData) {
            // Cache it locally
            QuestionDataLocalStorage.saveQuestionData(firebaseData);
            return firebaseData;
          }
        } catch (error) {
          // Silently handle 404 errors - question data might not exist yet
          if (error instanceof Error && error.message.includes('404')) {
            console.log('📄 No existing question data in Firebase for:', questionId);
          } else {
            console.error('Error loading question data from Firebase:', error);
          }
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }, [userInfo, tenantId, qbTestId, isWebViewMode, getStorageService]);
  
  const syncToFirebase = useCallback(async () => {
    if (isWebViewMode || !userInfo?.id || !tenantId || !qbTestId) return;
    
    try {
      const storageService = getStorageService();
      if (!storageService) return;
      
      const pendingSaves = getPendingSaves();
      
      // Sync each pending save
      for (const [questionId, data] of pendingSaves.entries()) {
        await storageService.saveQuestionData(
          userInfo.id,
          tenantId,
          qbTestId,
          questionId,
          data
        );
      }
      
      // Clear pending saves after successful sync
      clearPendingSaves();
    } catch (error) {
    }
  }, [userInfo, tenantId, qbTestId, isWebViewMode, getStorageService, getPendingSaves, clearPendingSaves]);
  
  return {
    saveQuestionData,
    loadQuestionData,
    syncToFirebase
  };
}